import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'

import ClickAwayListener from '@material-ui/core/ClickAwayListener'
import Grow from '@material-ui/core/Grow'
import MuiMenuItem from '@material-ui/core/MenuItem'
import MenuList from '@material-ui/core/MenuList'
import Paper from '@material-ui/core/Paper'
import Popper from '@material-ui/core/Popper'
import { styled } from '@material-ui/core/styles'

const MenuItemBase = forwardRef(({ ...props }, ref) => <MuiMenuItem ref={ref} {...props} />)

const StyledMenuItem = styled(MenuItemBase)(({ active, theme }) => ({
  backgroundColor: active ? theme.colors.sky[100] : 'white',
  color: active ? theme.palette.primary.main : theme.palette.text.secondary,
  display: 'flex',
  alignItems: 'center',
  padding: '12px 20px',
  lineHeight: 1.75,
  fontWeight: 500,
  verticalAlign: 'middle',
  borderRadius: 0,
  '&:hover': {
    backgroundColor: theme.colors.sky[100],
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('lg')]: {
    padding: '12px',
    fontSize: '1rem',
  },
}))

const StyledMenuList = styled(MenuList)(({ theme }) => ({
  padding: 0,
  boxShadow: theme.shadows[3],
  width: 158,
  fontSize: '1.125rem',
}))

export const MenuItem = forwardRef(({ className, href, ...props }, ref) => {
  const active = href === window.location.pathname

  return <StyledMenuItem ref={ref} active={active} className={className} href={href} {...props} />
})

const NavDropdownMenu = ({ children, anchor }) => {
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef(null)

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <>
      {anchor({
        open,
        ref: anchorRef,
        'aria-controls': open ? 'menu-list-grow' : undefined,
        'aria-haspopup': 'true',
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
      })}

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        modifiers={{
          preventOverflow: {
            enabled: true,
            padding: 0,
          },
        }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper elevation={0} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
              <ClickAwayListener onClickAway={handleClose}>
                <StyledMenuList id='menu-list-grow' onKeyDown={handleListKeyDown}>
                  {children}
                </StyledMenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

NavDropdownMenu.propTypes = {
  children: PropTypes.node,
  anchor: PropTypes.func,
}

export default NavDropdownMenu
